import Vue from "vue";
import Vuex from "vuex";
import axios from "./plugins/axios";
import moment, { now } from "moment";
import { gmapApi } from "vue2-google-maps";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    messages: [],
    maximized: false,
    fullscreenVideo: false,
    auth: null,
    me: null,
    event: null,
    selectedActivity: null,
    selectedActivityStartDate: null,
    loading: false,
    activityJoined: false,
    activityTeam: null,
    participants: [],
    participantsTotalCount: 0,
    serverTime: moment(),
    timeDiff: 0,
    openTokToken: null,
    openTokSession: null,
    openTokData: null,
    openTokApiKey: null,
    sessionId: null,
    sessionObject: null,
    participantId: null,
    participantChallengeDeclared: false,
    participantUsername: null,
    participantReconnected: false,
    textChatMessages: [],
    videoRoom: null,
    videoRoomOpenTokToken: null,
    videoRoomOpenTokSession: null,
    notifications: [],
    selfies: [],
    geolocation: {
      positionWatch: null,
      position: null,
      trackingEnabled: false,
      trackHistory: [],
      distance: 0,
      deniedByUser: false,
    },
    challengePerformance: null,
    isAutoJoining: false,
    videoMuted: false,
  },
  mutations: {
    LOADING(state, loading) {
      state.loading = !!loading;
    },
    SUCCESS(state, message) {
      state.messages.push({
        type: "success",
        message: message,
        timeout: 4000,
      });
    },
    ERROR(state, message) {
      state.messages.push({
        type: "error",
        message: message,
      });
    },
    WARNING(state, message) {
      state.messages.push({
        type: "warning",
        message: message,
      });
    },
    INFO(state, message) {
      state.messages.push({
        type: "info",
        message: message,
        timeout: 6000,
      });
    },
    NOTIFICATION(state, notification) {
      notification.date = notification.date || now();
      state.notifications.push(notification);
    },
    MESSAGE(state, [type, message, timeout]) {
      state.messages.push({
        type: type,
        message: message,
        timeout: timeout,
      });
    },
    I18N_MESSAGE(state, [type, key, params, timeout]) {
      state.messages.push({
        type: type,
        key: key,
        params: params,
        timeout: timeout,
      });
    },
    AUTH(state, auth) {
      state.auth = auth;
    },
    ME(state, me) {
      state.me = me;
    },
    ADD_ACTIVITY(state, activity) {
      let startDate;
      if (state.selectedActivity) {
        startDate = state.selectedActivity.date;
      }
      state.selectedActivity = activity;
      if (activity.event) state.event = activity.event;
      if (!state.selectedActivity.date && startDate) state.selectedActivity.date = startDate;
    },
    EVENT(state, event) {
      state.event = event;
    },
    UPDATE_ACTIVITY_START_DATE(state, startDate) {
      state.selectedActivity.date = startDate;
      state.selectedActivityStartDate = startDate;
    },
    UPDATE_ACTIVITY_VIDEO(state, videoLink) {
      state.selectedActivity.link = videoLink;
    },
    UPDATE_SCHEDULE_DURATION(state, payload) {
      let scheduleActivity = state.selectedActivity.schedule.find(
        (scheduleEntry) => scheduleEntry.id === payload.currentScheduleActivityId
      );
      if (scheduleActivity) scheduleActivity.durationSeconds = payload.durationSeconds;
    },
    SCHEDULE_UPDATED(state, payload) {
      state.selectedActivity.schedule = payload.newSchedule;
    },
    PARTICIPANTS_TOTAL_COUNT(state, totalCount) {
      state.participantsTotalCount = totalCount;
    },
    PARTICIPANTS_TOTAL_COUNT_INCREMENT(state) {
      state.participantsTotalCount++;
    },
    PARTICIPANT(state, participant) {
      state.participants.push(participant);
    },
    REMOVE_PARTICIPANT(state, participantId) {
      let toRemove = state.participants.findIndex(function (i) {
        return i.id === participantId;
      });
      state.participants.splice(toRemove, 1);
    },
    CLEAR_LATEST_PARTICIPANTS(state) {
      state.latestAddedParticipant = null;
      state.latestRemovedParticipant = null;
    },
    CLEAR_PARTICIPANTS(state) {
      state.participants = [];
    },
    SET_SERVER_TIME(state, time) {
      state.serverTime = time;
      state.timeDiff = time.diff(now());
    },
    INCREMENT_SERVER_TIME(state) {
      state.serverTime = moment().add(state.timeDiff, "ms");
    },
    OT_SESSION(state, session) {
      state.openTokSession = session;
    },
    OT_DATA(state, data) {
      state.openTokData = data;
    },
    OT_TOKEN(state, token) {
      state.openTokToken = token;
    },
    SESSION_ID(state, sessionId) {
      state.sessionId = sessionId;
    },
    SESSION_OBJECT(state, sessionObject) {
      state.sessionObject = sessionObject;
    },
    PARTICIPANT_ID(state, participantId) {
      state.participantId = participantId;
    },
    PARTICIPANT_CHALLENGE_DECLARED(state, value) {
      state.participantChallengeDeclared = value;
    },
    PARTICIPANT_USERNAME(state, participantUsername) {
      state.participantUsername = participantUsername;
    },
    PARTICIPANT_RECONNECTED(state, value) {
      state.participantReconnected = value;
    },
    ACTIVITY_LOADING(state, loading) {
      state.activityLoading = loading;
    },
    ACTIVITY_JOINED(state) {
      state.activityJoined = true;
    },
    ADD_TEAM(state, team) {
      state.activityTeam = team;
    },
    ADD_CHAT_MESSAGE(state, message) {
      state.textChatMessages.push(message);
    },
    REMOVE_CHAT_MESSAGE(state, messageId) {
      state.textChatMessages.splice(
        state.textChatMessages.findIndex((item) => item.id === messageId),
        1
      );
    },
    VIDEO_ROOM_OPENTOK_TOKEN(state, videoRoomOpenTokToken) {
      state.videoRoomOpenTokToken = videoRoomOpenTokToken;
    },
    VIDEO_ROOM_OPENTOK_SESSION(state, videoRoomOpenTokSession) {
      state.videoRoomOpenTokSession = videoRoomOpenTokSession;
    },
    VIDEO_ROOM(state, videoRoom) {
      state.videoRoom = videoRoom;
    },
    OT_APIKEY(state, openTokApiKey) {
      state.openTokApiKey = openTokApiKey;
    },
    GEOLOCATION_SET(state, position) {
      state.geolocation.position = { lat: position.coords.latitude, lng: position.coords.longitude };
      if (state.geolocation.trackingEnabled) {
        if (state.geolocation.trackHistory.length) {
          const lastPosition = state.geolocation.trackHistory[state.geolocation.trackHistory.length - 1];
          if (gmapApi) {
            const gMaps = gmapApi().maps;
            let distance = gMaps.geometry.spherical.computeDistanceBetween(
              new gMaps.LatLng(lastPosition.lat, lastPosition.lng),
              new gMaps.LatLng(position.coords.latitude, position.coords.longitude)
            );
            if (distance > 10) {
              state.geolocation.distance += distance;
              state.geolocation.trackHistory.push(state.geolocation.position);
            }
          }
        } else {
          state.geolocation.trackHistory.push(state.geolocation.position);
        }
      }
    },
    DISTANCE_SET(state, distance) {
      state.geolocation.distance = distance;
    },
    POSITION_WATCH(state, positionWatch) {
      state.geolocation.positionWatch = positionWatch;
    },
    TRACKING_ENABLED(state, trackingEnabled) {
      state.geolocation.trackingEnabled = trackingEnabled;
    },
    MAXIMIZED(state, maximized) {
      state.maximized = state.fullscreenVideo || maximized;
      if (state.maximized) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    },
    FULLSCREEN_VIDEO(state, fullscreenVideo) {
      state.fullscreenVideo = fullscreenVideo;
    },
    CHALLENGE_PERFORMANCE(state, challengePerformance) {
      state.challengePerformance = challengePerformance;
    },
    GEOLOCATION_DENIED_BY_USER(state, result) {
      state.geolocation.deniedByUser = result;
    },
    ADD_SELFIE(state, selfie) {
      state.selfies.push(selfie);
    },
    IS_AUTO_JOINING(state, isAutoJoining) {
      state.isAutoJoining = !!isAutoJoining;
    },
    VIDEO_MUTED(state, muted){
      state.videoMuted = muted;
    }
  },
  actions: {
    TOGGLE_FULLSCREEN({ state, commit }) {
      commit("FULLSCREEN_VIDEO", !state.fullscreenVideo);
      commit("MAXIMIZED", state.fullscreenVideo || state.videoRoom);
    },
    ENABLE_GEOLOCATION({ state, commit, dispatch }) {
      return new Promise((resolve, reject) => {
        let first = true;
        commit(
          "POSITION_WATCH",
          navigator.geolocation.watchPosition(
            (loc) => {
              commit("GEOLOCATION_SET", loc);
              if (first) {
                try {
                  axios
                    .post(
                      `/event/activities/session/${state.sessionId}/participant/${state.participantId}/geolocation`,
                      { latitude: loc.coords.latitude, longitude: loc.coords.longitude }
                    )
                    .catch((err) => console.error(err));
                } catch (err) {
                  console.error(err);
                }
                resolve(loc);
              }
              first = false;
              commit("GEOLOCATION_DENIED_BY_USER", false);
            },
            (error) => {
              if (first) {
                dispatch("DISABLE_GEOLOCATION");
                commit("GEOLOCATION_DENIED_BY_USER", true);
                reject(error);
              }
              first = false;
            },
            {
              enableHighAccuracy: true,
            }
          )
        );
      });
    },
    DISABLE_GEOLOCATION({ commit, state }) {
      if (state.geolocation.positionWatch !== null) {
        navigator.geolocation.clearWatch(state.geolocation.positionWatch);
        commit("POSITION_WATCH", null);
      }
    },
    async LOAD_SERVER_TIME({ commit }) {
      setInterval(() => commit("INCREMENT_SERVER_TIME"), 1000);
      try {
        let serverTime = await axios.get("/event/getTime");
        commit("SET_SERVER_TIME", moment(serverTime.date));
      } catch (err) {
        console.log("Failed to load server time");
      }
    },
    async AUTHENTICATE({ commit, state }, auth) {
      /**
       * Load auth if not passed in parameters
       */
      if (typeof auth === "undefined") {
        if (state.auth) {
          auth = state.auth;
        } else if (!!window.localStorage && typeof localStorage.getItem === "function") {
          auth = JSON.parse("" + localStorage.getItem("auth"));
        }
      }
      /**
       * Calculate expires_date if not set
       */
      if (auth && !auth.expires_date) {
        auth.expires_date = new Date().getTime() + (auth.expires_in || 14400) * 1000;
      }
      /**
       * Check validity of auth token
       */
      if (!auth || auth.expires_date < new Date().getTime() + 60000) {
        commit("AUTH", null);
        commit("ME", null);
        axios.defaults.headers.common = null;
        if (!!window.localStorage && typeof localStorage.removeItem === "function") {
          localStorage.removeItem("auth");
        }
        return null;
      }
      /**
       * Don't load me from server if already loaded and auth still valid
       */
      if (state.auth === auth && state.me) {
        return state.me;
      }
      /**
       * Load me from server
       */
      commit("AUTH", auth);
      axios.defaults.headers.common = {
        Authorization: `Bearer ${auth.access_token}`,
      };
      if (!!window.localStorage && typeof localStorage.setItem === "function") {
        localStorage.setItem("auth", JSON.stringify(auth));
      }
      commit("ME", await axios.get("/me"));
      return state.me;
    },
  },
});
